import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Plan } from "components/Pricing/types";
import { COLORS } from "components/Pricing/constants";
import { neutral } from "theme/palette";
import { BulletPreview } from "./BulletPreview";
import { SelectButton } from "./SelectButton";

type PricingSegmentCardProps = {
  title: string;
  subtitle?: string;
  plans: Plan[];
  disabled?: boolean;
  disabledMessage?: string;
};

export const PricingSegmentCard = ({
  title,
  subtitle,
  plans,
  disabled,
  disabledMessage,
}: PricingSegmentCardProps) => (
  <Card
    elevation={0}
    sx={{
      position: "relative",
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "background.paper",
      boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.primary.main, 0.04)}`,
      overflow: "hidden",
      height: "auto",
      display: "flex",
      flexDirection: "column",
    }}
  >
    {/* Header */}
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        sx={{
          color: neutral.ink,
          fontWeight: 900,
          letterSpacing: 1,
          textTransform: "uppercase",
          fontSize: 18,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "primary.main",
          fontWeight: 700,
          display: "block",
          minHeight: 18,
          fontSize: 14,
        }}
      >
        {subtitle ?? "\u00A0"}
      </Typography>
    </Box>

    <CardContent sx={{ p: 0, flexGrow: 1, display: "flex", flexDirection: "column" }}>
      {/* Column headers (hidden on mobile for simplicity) */}
      <Box
        sx={{
          display: { xs: "none", sm: "grid" },
          gridTemplateColumns: "1.6fr 2.2fr 1fr 140px",
          gap: 2,
          px: 3,
          py: 1.2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" sx={{ color: neutral.ink, fontWeight: 800, fontSize: 13 }}>
          Plan
        </Typography>
        <Typography variant="caption" sx={{ color: neutral.ink, fontWeight: 800, fontSize: 13 }}>
          Includes
        </Typography>
        <Typography variant="caption" sx={{ color: neutral.ink, fontWeight: 800, fontSize: 13 }}>
          Price
        </Typography>
        <Box />
      </Box>

      {/* Rows */}
      <Stack divider={<Divider sx={{ borderColor: "divider" }} />} sx={{ flexGrow: 1 }}>
        {plans.map((p) => (
          <Box
            key={p.id}
            sx={{
              px: 3,
              py: 2,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1.6fr 2.2fr 1fr 140px" },
              gap: { xs: 1.5, sm: 2 },
              alignItems: "center",
              backgroundColor: p.highlight ? (theme: any) => alpha(theme.palette.primary.main, 0.06) : "transparent",
              borderRadius: p.highlight ? 2 : 0,
            }}
          >
            {/* Plan name + badge */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography
                sx={{ color: neutral.ink, fontWeight: 900, fontSize: 16, maxWidth: 100, textAlign: "left" }}
              >
                {p.title}
              </Typography>

              {p.subheader && (
                <Chip
                  icon={<AutoAwesomeIcon />}
                  label={p.subheader}
                  size="small"
                  sx={{
                    backgroundColor: COLORS.check,
                    color: neutral.ink,
                    fontWeight: 800,
                    fontSize: 10,
                    width: 100,
                    whiteSpace: "normal",
                    "& .MuiChip-icon": { color: neutral.ink },
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      lineHeight: 1.0,
                      textAlign: "center",
                      paddingY: 1,
                    },
                  }}
                />
              )}
            </Box>

            {/* Mobile price summary */}
            <Box
              sx={{
                display: { xs: "flex", sm: "none" },
                mt: 0.5,
                textAlign: "left",
                alignItems: "baseline",
                gap: 0.6,
              }}
            >
              <Typography
                  sx={{ color: neutral.ink, fontWeight: 900, fontSize: 15, textAlign: "left" }}
              >
                {p.price} {p.currency ?? "DKK"}
              </Typography>
              {p.billingNote && (
                <Typography
                  variant="caption"
                  sx={{ color: "primary.main", fontWeight: 700, fontSize: 13, textAlign: "left" }}
                >
                  {p.billingNote}
                </Typography>
              )}
            </Box>

            {/* Includes */}
            <Box>
              <BulletPreview items={p.description} />
            </Box>

            {/* Price (desktop) */}
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                sx={{ color: neutral.ink, fontWeight: 900, fontSize: 20, lineHeight: 1.1 }}
              >
                {p.price} {p.currency ?? "DKK"}
              </Typography>
              {p.billingNote && (
                <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700, fontSize: 13 }}>
                  {p.billingNote}
                </Typography>
              )}
            </Box>

            {/* CTA */}
            <Box sx={{ justifySelf: { xs: "stretch", sm: "end" } }}>
              <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                <SelectButton plan={p} />
              </Box>
            </Box>
          </Box>
        ))}
      </Stack>
    </CardContent>

    {/* Disabled overlay */}
    {disabled && disabledMessage && (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: (theme) => alpha(theme.palette.common.white, 0.50),
          backdropFilter: "blur(3px)",
          zIndex: 2,
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            px: 3,
            py: 2,
            borderRadius: 2,
            background: (theme) => alpha(theme.palette.common.white, 0.96),
            boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: 15, sm: 19 },
              color: "primary.main",
              letterSpacing: 0.3,
            }}
          >
            {disabledMessage}
          </Typography>
        </Box>
      </Box>
    )}
  </Card>
);
